import { useEffect } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  useReportStore,
  BEAT_HR_TAB_ID,
} from '@/entities/report';

/**
 * Wires the add-ECG-bookmark button on the beats tab together with its keyboard shortcut. Exposes a click handler that opens the add bookmark modal only when it is closed, and registers a window keydown listener firing the same handler on the F key while the pointer hovers the beats tab zone, ignoring auto-repeat and Ctrl or Meta combinations.
 *
 * @returns An object exposing the add-bookmark click handler.
 */
const useButtonAddEcgBookmark = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab modal control flags into the report store. Forwards the value to the shared setControlModal action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the beats tab modal control flags from the report store without subscribing. Applies the tab-keyed control modal selector to the current store snapshot, so click and key handlers see the flags as they are right now.
   *
   * @returns The current control modal flag set for the beats tab.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());

  /**
   * Opens the add ECG bookmark modal on the beats tab. Reads the current modal flags and, only when the bookmark modal is closed, writes the default control modal value with isShowAddEcgBookmark enabled so any other open modal closes.
   *
   * @returns Nothing.
   */
  const onClickAddBookmark = () => {
    if (!getControlModal().isShowAddEcgBookmark) {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddEcgBookmark: true });
    }
  };

  useEffect(() => {
    /**
     * Triggers the add ECG bookmark shortcut from a window keydown. Ignores auto-repeat, presses made while the beats tab zone is not hovered, and any Ctrl or Meta combination, then opens the bookmark modal on key code 70, the F key.
     *
     * @param event - Keydown event carrying the key code and the modifier flags.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      if (keyCode === 70) {
        // F
        onClickAddBookmark();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    onClickAddBookmark,
  };
};

export default useButtonAddEcgBookmark;
